import { Injectable, computed, signal } from '@angular/core';
import { NavigationTab, NavigationTabCatalogEntry } from '../../../models/navigation/navigation-tab.model';

/** Identificador y URL de la pestaña fija de inicio. */
export const HOME_TAB_ID = '/home';

/** Máximo de pestañas cerrables simultáneas. No incluye la pestaña fija de Inicio. */
export const MAX_TABS = 5;

@Injectable({ providedIn: 'root' })
export class NavigationTabsService {
  private readonly homeTab: NavigationTab = {
    id: HOME_TAB_ID,
    title: 'Inicio',
    url: HOME_TAB_ID,
    icon: 'home',
    closable: false,
  };

  private readonly _tabs = signal<NavigationTab[]>([{ ...this.homeTab }]);

  private readonly _activeId = signal<string>(HOME_TAB_ID);

  readonly tabs = this._tabs.asReadonly();
  readonly activeId = this._activeId.asReadonly();

  /** Pestañas cerrables abiertas actualmente (excluye Inicio). */
  readonly abiertasCerrables = computed(() => this._tabs().filter((tab) => tab.closable).length);

  /** Útil para deshabilitar opciones o mostrar un contador en la UI. */
  readonly limiteAlcanzado = computed(() => this.abiertasCerrables() >= MAX_TABS);

  /**
   * Catálogo original obtenido desde el menú.
   */
  private rawCatalog = new Map<string, NavigationTabCatalogEntry>();

  /**
   * Catálogo filtrado según permisos.
   */
  private catalog = new Map<string, NavigationTabCatalogEntry>();

  /**
   * Rutas del catálogo ordenadas desde la más específica
   * hasta la menos específica.
   */
  private catalogKeys: string[] = [];

  /**
   * Rutas permitidas del usuario.
   */
  private permittedRoutes: string[] = [];

  // ---------------------------------------------------------------------------
  // CATÁLOGO
  // ---------------------------------------------------------------------------

  setCatalog(entries: ReadonlyMap<string, NavigationTabCatalogEntry>): void {
    this.rawCatalog.clear();

    entries.forEach((value, key) => {
      this.rawCatalog.set(key, value);
    });

    this.rebuildCatalog();
  }

  setPermittedRoutes(routes: unknown): void {
    const lista = Array.isArray(routes) ? routes : [];

    this.permittedRoutes = lista
      .filter((ruta): ruta is string => typeof ruta === 'string' && ruta.trim().length > 0)
      .map((ruta) => {
        const limpia = ruta.trim();

        return this.normalize(limpia.startsWith('/') ? limpia : `/${limpia}`);
      });

    this.rebuildCatalog();
    this.pruneUnauthorizedTabs();
  }

  private isPermitted(path: string): boolean {
    if (!this.permittedRoutes.length) {
      return true;
    }

    if (path === HOME_TAB_ID) {
      return true;
    }

    return this.permittedRoutes.some((ruta) => path === ruta || path.startsWith(`${ruta}/`));
  }

  /**
   * IMPORTANTE:
   * Se utiliza Map.forEach() y Array.from()
   * para evitar TS2802 con MapIterator.
   */
  private rebuildCatalog(): void {
    const efectivo = new Map<string, NavigationTabCatalogEntry>();

    this.rawCatalog.forEach((entrada, id) => {
      if (this.isPermitted(id)) {
        efectivo.set(id, entrada);
      }
    });

    this.catalog = efectivo;

    this.catalogKeys = Array.from(efectivo.keys()).sort((a: string, b: string): number => b.length - a.length);
  }

  private pruneUnauthorizedTabs(): void {
    if (!this.permittedRoutes.length) {
      return;
    }

    const activo = this._activeId();

    this._tabs.update((tabs) => {
      const filtradas = tabs.filter((tab) => !tab.closable || tab.id === activo || this.isPermitted(tab.id));

      return filtradas.length === tabs.length ? tabs : filtradas;
    });
  }

  // ---------------------------------------------------------------------------
  // SINCRONIZACIÓN CON ROUTER
  // ---------------------------------------------------------------------------

  sync(url: string, baseId: string | null, fallbackTitle?: string): void {
    const id = this.resolveTabId(url, baseId);

    if (!id || !this.isPermitted(id)) {
      return;
    }

    const meta = this.catalog.get(id);

    const title = meta?.title ?? fallbackTitle ?? this.titleFromId(id);

    this._tabs.update((tabs) => {
      const index = tabs.findIndex((tab) => tab.id === id);

      if (index === -1) {
        const nueva: NavigationTab = {
          id,
          title,
          url,
          icon: meta?.icon,
          svgIcon: meta?.svgIcon,
          closable: id !== HOME_TAB_ID,
        };

        return [...tabs, nueva];
      }

      const actual = tabs[index];

      if (actual.url === url && actual.title === title) {
        return tabs;
      }

      const siguiente = [...tabs];

      siguiente[index] = {
        ...actual,
        url,
        title,
      };

      return siguiente;
    });

    this._activeId.set(id);
  }

  /**
   * Indica si la navegación a `url` respeta el límite de pestañas.
   *
   * Devuelve true si la ruta no genera pestaña, si no está permitida (ese
   * filtro le corresponde a los guards de permisos), si la pestaña ya está
   * abierta, o si todavía queda cupo.
   */
  puedeAbrir(url: string, baseId: string | null = null): boolean {
    const id = this.resolveTabId(url, baseId);

    if (!id || !this.isPermitted(id)) {
      return true;
    }

    if (this._tabs().some((tab) => tab.id === id)) {
      return true;
    }

    return this.abiertasCerrables() < MAX_TABS;
  }

  /**
   * Obtiene el id de pestaña correspondiente a la URL.
   */
  private resolveTabId(url: string, baseId: string | null): string | null {
    const path = this.normalize(url);

    if (!path || path === '/') {
      return null;
    }

    if (path === HOME_TAB_ID) {
      return HOME_TAB_ID;
    }

    for (let i = 0; i < this.catalogKeys.length; i++) {
      const clave = this.catalogKeys[i];

      if (path === clave || path.startsWith(`${clave}/`)) {
        return clave;
      }
    }

    return baseId ?? path;
  }

  // ---------------------------------------------------------------------------
  // CIERRE DE PESTAÑAS
  // ---------------------------------------------------------------------------

  closeInactive(id: string): boolean {
    const tabs = this._tabs();

    const objetivo = tabs.find((tab) => tab.id === id);

    if (!objetivo?.closable || id === this._activeId()) {
      return false;
    }

    this._tabs.set(tabs.filter((tab) => tab.id !== id));

    return true;
  }

  getCloseTarget(id: string): NavigationTab | null {
    const tabs = this._tabs();

    const index = tabs.findIndex((tab) => tab.id === id);

    if (index === -1 || !tabs[index].closable) {
      return null;
    }

    const restantes = tabs.filter((tab) => tab.id !== id);

    return restantes[index - 1] ?? restantes[index] ?? { ...this.homeTab };
  }

  confirmClose(id: string, fallbackActiveId: string): void {
    this._tabs.update((tabs) => {
      const restantes = tabs.filter((tab) => tab.id !== id);

      return restantes.length ? restantes : [{ ...this.homeTab }];
    });

    if (this._activeId() === id) {
      this._activeId.set(fallbackActiveId);
    }
  }

  // ---------------------------------------------------------------------------
  // SESIÓN
  // ---------------------------------------------------------------------------

  reset(): void {
    this.rawCatalog.clear();
    this.catalog.clear();

    this.catalogKeys = [];
    this.permittedRoutes = [];

    this._tabs.set([{ ...this.homeTab }]);

    this._activeId.set(HOME_TAB_ID);
  }

  // ---------------------------------------------------------------------------
  // UTILIDADES
  // ---------------------------------------------------------------------------

  private normalize(url: string): string {
    const path = url.split('?')[0].split('#')[0];

    if (path.length > 1 && path.endsWith('/')) {
      return path.slice(0, -1);
    }

    return path;
  }

  private titleFromId(id: string): string {
    const partes = id.split('/').filter(Boolean);

    const segmento = partes.length > 0 ? partes[partes.length - 1] : '';

    if (!segmento) {
      return 'Sin título';
    }

    return segmento.replace(/-/g, ' ').replace(/\b\w/g, (letra) => letra.toUpperCase());
  }
}
